'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import { captureBrowserEvent } from '@/lib/analytics/client-capture';
import { sanitizeHref } from '@/lib/analytics/sanitize';
import type { ResumeSessionState } from '@/lib/resume-session';
import { readResumeSession } from '@/lib/resume-session';
import { cn } from '@/lib/utils';

import { Button } from '../ui/button';

interface CidLinkProps {
  children: React.ReactNode;
  className?: string;
  href?: string;
  onNavigate?: () => void;
}

export default function CidLink({ children, className, href = '/', onNavigate }: CidLinkProps) {
  const router = useRouter();
  const [resumeSession, setResumeSession] = useState<ResumeSessionState | null>(null);

  useEffect(() => {
    setResumeSession(readResumeSession());

    const handleResumeSessionChanged = (event: Event) => {
      const customEvent = event as CustomEvent<ResumeSessionState>;

      if (customEvent.detail) {
        setResumeSession(customEvent.detail);
        return;
      }

      setResumeSession(readResumeSession());
    };

    window.addEventListener('resume-session-changed', handleResumeSessionChanged);

    return () => {
      window.removeEventListener('resume-session-changed', handleResumeSessionChanged);
    };
  }, []);

  if (!resumeSession) {
    return null;
  }

  const targetHref =
    resumeSession.href && resumeSession.href !== '/'
      ? resumeSession.href
      : `/${resumeSession.cid}${href}`;

  return (
    <Button
      className={cn('bg-aareal-aval hover:bg-aareal-aval-light', className)}
      data-testid="cid-link"
      onClick={() => {
        captureBrowserEvent('marketing_cta_clicked', {
          location: 'header_resume',
          link_type: 'resume',
          href: sanitizeHref(targetHref),
        });

        onNavigate?.();
        router.push(targetHref);
      }}
      type="button"
    >
      {children}
    </Button>
  );
}
